// src/pages/account/ChangePasswordPage.jsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "../../utils/apparifySwal";

import { useAuth } from "../../context/AuthContext";
import { userAPI } from "../../services/profileService";
import { useAccountSwal } from "../../features/account/useAccountSwal";
import PasswordInput from "../../components/PasswordInput";

function clean(v) {
  return String(v ?? "").trim();
}

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { swalBase } = useAccountSwal();

  const [pwSaving, setPwSaving] = useState(false);
  const [pw, setPw] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const savePassword = async () => {
    const currentPassword = clean(pw.currentPassword);
    const newPassword = clean(pw.newPassword);
    const confirmPassword = clean(pw.confirmPassword);

    if (!currentPassword || !newPassword) {
      await Swal.fire({
        ...swalBase,
        icon: "warning",
        title: "INCOMPLETE",
        html: "Please enter your current and new password.",
        confirmButtonText: "OK",
      });
      return;
    }

    if (newPassword.length < 8) {
      await Swal.fire({
        ...swalBase,
        icon: "warning",
        title: "WEAK PASSWORD",
        html: "Use at least 8 characters.",
        confirmButtonText: "OK",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      await Swal.fire({
        ...swalBase,
        icon: "warning",
        title: "DOESN’T MATCH",
        html: "Confirm password does not match.",
        confirmButtonText: "OK",
      });
      return;
    }

    setPwSaving(true);

    Swal.fire({
      ...swalBase,
      title: "SAVING…",
      html: "Updating your password",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      await userAPI.changePassword({ currentPassword, newPassword });

      Swal.close();

      await Swal.fire({
        ...swalBase,
        icon: "success",
        title: "UPDATED",
        html: "Password changed successfully.",
        timer: 1200,
        showConfirmButton: false,
      });

      setPw({ currentPassword: "", newPassword: "", confirmPassword: "" });
      navigate("/account/change-password");
    } catch (e) {
      Swal.close();

      const msg = e?.message || "Please try again.";
      if (msg.includes("401")) {
        logout();
        navigate("/login", { replace: true });
        return;
      }

      await Swal.fire({
        ...swalBase,
        icon: "error",
        title: "CHANGE FAILED",
        html: msg,
        confirmButtonText: "OK",
      });
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">Change password</div>
      </div>

      <div className="mt-3 space-y-3">
        <PasswordInput
          autoFocus
          placeholder="Current password"
          value={pw.currentPassword}
          onChange={(e) => setPw((p) => ({ ...p, currentPassword: e.target.value }))}
          className="w-full rounded-xl border border-neutral-200 bg-neutral-50/60 px-4 py-3 text-sm text-neutral-900 outline-none transition focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-neutral-700 dark:bg-neutral-800/60 dark:text-neutral-50 dark:placeholder-neutral-500"
        />

        <PasswordInput
          placeholder="New password (min 8 chars)"
          value={pw.newPassword}
          onChange={(e) => setPw((p) => ({ ...p, newPassword: e.target.value }))}
          className="w-full rounded-xl border border-neutral-200 bg-neutral-50/60 px-4 py-3 text-sm text-neutral-900 outline-none transition focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-neutral-700 dark:bg-neutral-800/60 dark:text-neutral-50 dark:placeholder-neutral-500"
        />

        <PasswordInput
          placeholder="Confirm new password"
          value={pw.confirmPassword}
          onChange={(e) => setPw((p) => ({ ...p, confirmPassword: e.target.value }))}
          className="w-full rounded-xl border border-neutral-200 bg-neutral-50/60 px-4 py-3 text-sm text-neutral-900 outline-none transition focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-neutral-700 dark:bg-neutral-800/60 dark:text-neutral-50 dark:placeholder-neutral-500"
        />
      </div>

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex-1 rounded-xl border border-neutral-200 px-5 py-3 text-xs font-semibold tracking-[0.20em] text-neutral-600 transition hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
        >
          CANCEL
        </button>

        <button
          type="button"
          disabled={pwSaving}
          onClick={savePassword}
          className="flex-1 rounded-xl bg-neutral-900 px-5 py-3 text-xs font-semibold tracking-[0.20em] text-white transition hover:bg-neutral-800 disabled:opacity-50 dark:bg-neutral-50 dark:text-neutral-900 dark:hover:bg-neutral-200"
        >
          {pwSaving ? "SAVING…" : "SAVE"}
        </button>
      </div>
    </div>
  );
}
